import type {
  ActivityEntry,
  AddressesResponse,
  AdminUser,
  AutocompleteResponse,
  ContractorCoverage,
  ContractorDetailResponse,
  ContractorSearchResponse,
  CountsResponse,
  CountyCoverage,
  CoverageStats,
  CreateUserBody,
  ExploreLevel,
  ExplorePlace,
  ExplorePlaceRef,
  GeocodeResult,
  JurisdictionsResponse,
  Me,
  ParcelCoverageSummary,
  ParcelsResponse,
  PermitCoverage,
  PermitGraphResponse,
  PermitsResponse,
  PlanningCoverage,
  PlanningDetailResponse,
  PlanningSearchResponse,
  ProspectJurisdiction,
  TagExample,
  TagStats,
  UpdateUserBody,
} from '@mollusk/shared';
import { TERMS_VERSION } from '@mollusk/shared';
import type { SampleData } from '../features/sample/SampleDataView';
import { ApiClient } from './client';

/** A bundled Census geometry file (TopoJSON for counties/states, GeoJSON for jurisdictions). */
export type CoverageGeo = 'counties' | 'states' | 'permit-jurisdictions';

const client = new ApiClient({ baseUrl: '/api' });

export const api = {
  requestSignInLink: (email: string) => client.post<{ ok: true }>('/auth/magic-link', { email }),
  verifySignIn: (token: string) => client.post<{ ok: true }>('/auth/verify', { token }),
  acceptTerms: () => client.post<{ ok: true }>('/terms/accept', { version: TERMS_VERSION }),
  logout: () => client.post<{ ok: true }>('/auth/logout', {}),
  me: () => client.get<Me>('/auth/me'),

  searchPermits: (args: { kind: 'permit' | 'apn'; q: string; state?: string }) =>
    client.get<PermitsResponse>('/search/permits', { params: args }),
  tagExamples: (tag: string) =>
    client.get<{ pinned: boolean; examples: TagExample[] }>(
      `/tags/${encodeURIComponent(tag)}/examples`,
    ),
  pinTagExample: (tag: string, permitId: string) =>
    client.put<{ ok: true }>(`/admin/tags/${encodeURIComponent(tag)}/pin`, { permit_id: permitId }),
  unpinTagExample: (tag: string) =>
    client.del<{ ok: true }>(`/admin/tags/${encodeURIComponent(tag)}/pin`),
  explorePlaces: (level: ExploreLevel, q: string) =>
    client.get<{ places: ExplorePlaceRef[] }>('/explore/places', { params: { level, q } }),
  explorePlace: (ref: { level: ExploreLevel; state: string; id: string }) =>
    client.get<ExplorePlace>('/explore/place', { params: ref }),
  sampleManifest: () => client.get<SampleData>('/sample/manifest'),
  tagStats: () => client.get<Omit<TagStats, 'table'>>('/tags/stats'),
  coverageStats: () => client.get<CoverageStats>('/coverage/stats'),

  adminUsers: () => client.get<{ users: AdminUser[] }>('/admin/users'),
  adminCreateUser: (body: Partial<CreateUserBody> & { email: string }) =>
    client.post<AdminUser>('/admin/users', body),
  adminUpdateUser: (id: string, patch: UpdateUserBody) =>
    client.patch<AdminUser>(`/admin/users/${id}`, patch),
  adminResendInvite: (id: string) => client.post<{ ok: true }>(`/admin/users/${id}/invite`, {}),
  adminActivity: (params: { user_id?: string; kind?: string; limit?: string }) =>
    client.get<{ activity: ActivityEntry[] }>('/admin/activity', { params }),

  autocomplete: (q: string, sessionToken: string, proximity?: string) =>
    client.get<AutocompleteResponse>('/autocomplete', {
      params: { q, session_token: sessionToken, proximity },
    }),

  geocodeById: (mapboxId: string, sessionToken: string) =>
    client.get<GeocodeResult>('/geocode', {
      params: { mapbox_id: mapboxId, session_token: sessionToken },
    }),

  geocodeByQuery: (q: string) => client.get<GeocodeResult>('/geocode', { params: { q } }),

  parcels: (bbox: [number, number, number, number]) =>
    client.get<ParcelsResponse>('/parcels', { params: { bbox: bbox.join(',') } }),

  permits: (args: {
    lat: number;
    lng: number;
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    apn?: string;
  }) =>
    client.get<PermitsResponse>('/permits', {
      params: {
        lat: String(args.lat),
        lng: String(args.lng),
        line1: args.line1,
        // Locality of the searched address — pins the line1 match to this city
        // so an identical street line elsewhere doesn't bleed in (Austin vs
        // Rosenberg "4310 Avenue H").
        city: args.city,
        state: args.state,
        postal_code: args.postal_code,
        apn: args.apn,
      },
    }),
  permitGraph: (permitId: string, sourceId: string) =>
    client.get<PermitGraphResponse>(`/permits/${encodeURIComponent(permitId)}`, {
      params: { source_id: sourceId },
    }),

  counts: (args: { street: string; city: string; state: string; postal_code: string }) =>
    client.get<CountsResponse>('/counts', { params: args }),

  addresses: (
    args:
      | { scope: 'street'; street: string; city: string; state: string; postal_code: string }
      | { scope: 'city'; city: string; state: string }
      | { scope: 'zip'; postal_code: string },
  ) => client.get<AddressesResponse>('/addresses', { params: args }),

  coverageParcels: () => client.get<ParcelCoverageSummary>('/coverage/parcels'),
  coverageCounty: (fips: string) => client.get<CountyCoverage>(`/coverage/parcels/${fips}`),
  coveragePermits: () => client.get<PermitCoverage>('/coverage/permits'),
  coverageJurisdictions: () =>
    client.get<{ generated_at: string; jurisdictions: ProspectJurisdiction[] }>(
      '/coverage/jurisdictions',
    ),
  coveragePlanning: () => client.get<PlanningCoverage>('/coverage/planning'),
  coverageContractors: () => client.get<ContractorCoverage>('/coverage/contractors'),
  coverageGeo: (name: CoverageGeo) => client.get<unknown>(`/coverage/geo/${name}`),

  planningSearch: (q: string, state?: string, family?: string) =>
    client.get<PlanningSearchResponse>('/planning/search', { params: { q, state, family } }),
  planningDetail: (caseId: string) =>
    client.get<PlanningDetailResponse>(`/planning/${encodeURIComponent(caseId)}`),
  contractorSearch: (q: string, state?: string, authority?: string) =>
    client.get<ContractorSearchResponse>('/contractors/search', {
      params: { q, state, authority },
    }),
  contractorDetail: (kind: 'entity' | 'credential', id: string) =>
    client.get<ContractorDetailResponse>(`/contractors/${kind}/${encodeURIComponent(id)}`),

  jurisdictions: () => client.get<JurisdictionsResponse>('/jurisdictions'),
};
